import React, { useEffect, useRef } from "react";

import { useAuth } from "@/stores/auth/auth-store";
import { useHomeProjectRedirect } from "@/hooks/useHomeProjectRedirect";

interface PublicRouteProps {
  children: React.ReactNode;
  allowAuthenticated?: boolean;
}

// Public auth routes normally send authenticated sessions back into the app.
// Token-link pages can opt out so an existing session does not block submission.
export const PublicRoute: React.FC<PublicRouteProps> = ({
  children,
  allowAuthenticated = false,
}) => {
  const { user, isAuthenticated, isLoading, initialized } = useAuth();
  const { performRedirect } = useHomeProjectRedirect();
  const redirectStarted = useRef(false);

  useEffect(() => {
    // Wait for `user` as well as `isAuthenticated`: performRedirect reads the
    // user and returns early without navigating when it is null, so firing
    // before the profile loads would latch redirectStarted and never retry.
    if (
      allowAuthenticated ||
      !initialized ||
      isLoading ||
      !isAuthenticated ||
      !user ||
      redirectStarted.current
    ) {
      return;
    }

    redirectStarted.current = true;
    void performRedirect();
  }, [
    allowAuthenticated,
    initialized,
    isLoading,
    isAuthenticated,
    user,
    performRedirect,
  ]);

  // Wait for auth store to initialize before making routing decisions
  if (!initialized || isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (isAuthenticated && !allowAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return <>{children}</>;
};
